import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { ArrowLeft, Download, FileText, Package, Paperclip, Pencil, Plus, Tag, Wallet } from 'lucide-react';
import apiService from '../../services/api';
import WeightToggle, { BagWeight, useShowWeight } from '../../components/admin/WeightToggle';
import CuentaCorrienteDetalle from './CuentaCorrienteDetalle';
import SupplierReceiptModal from '../../components/admin/SupplierReceiptModal';
import SupplierPriceList from '../../components/admin/SupplierPriceList';
import { formatARS } from '../../utils/formatCurrency';
import { toProductImageUrl } from '../../utils/imageUrl';
import { useAuth } from '../../contexts/AuthContext';
import { PACK_SIZE, COLOR_LABELS, receiptColorStyle } from '../../utils/warehouseStock';
import { ColorKey } from '../../utils/pricingConfig';
import { bagWeightGrams, formatBagKg, formatKg } from '../../utils/bagWeight';

interface Supplier {
  id: number;
  name: string;
  contactName: string;
  phone: string;
  email: string;
  cuit: string;
  isActive: boolean;
}

interface SupplierReceiptItem {
  ancho: number;
  largo: number;
  color: string;
  microns: number;
  unidad: 'bolsas' | 'packs';
  cantidad: number;
  bolsasPorPack: number | null;
  totalBolsas: number;
  unitPrice: number | null;
  subtotal?: number | null;
  priceIsFigurative?: boolean;
  priceFigurativeNote?: string | null;
  itemNote: string | null;
  loadStock?: boolean;
}

interface SupplierReceiptSummary {
  id: number;
  receiptNumber: string;
  receiptDate: string;
  amount: number;
  note: string;
  attachmentPath: string;
  items: SupplierReceiptItem[];
  hasNonStockItem?: boolean;
  hasFigurativePriceItem?: boolean;
  amountMismatch?: boolean;
}

const formatDMY = (iso: string): string => {
  const [y, m, d] = String(iso || '').split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const receiptItemBags = (item: SupplierReceiptItem) => Number(item.totalBolsas ?? (item.unidad === 'packs' ? item.cantidad * Number(item.bolsasPorPack || PACK_SIZE) : item.cantidad));
const CATEGORY_LABELS: Record<string, string> = { cheque: 'Cheque', efectivo: 'Efectivo', transferencia: 'Transferencia', remito: 'Remito', factura: 'Factura', nota_credito: 'Nota de crédito', otro: 'Otro' };
const CHECK_STATUS_LABELS: Record<string, string> = { pendiente: 'Pendiente', depositado: 'Depositado, sin acreditar', cobrado: 'Acreditado / debitado', rechazado: 'Rechazado' };

const addSheet = (workbook: XLSX.WorkBook, name: string, rows: Array<Record<string, string | number>>) => {
  const data = rows.length ? rows : [{ 'Sin datos': '' }];
  const sheet = XLSX.utils.json_to_sheet(data);
  const columns = Object.keys(data[0]);
  sheet['!cols'] = columns.map((key) => ({ wch: Math.min(60, Math.max(key.length, ...data.map((row) => String(row[key] ?? '').length)) + 2) }));
  if (rows.length) sheet['!autofilter'] = { ref: sheet['!ref'] || 'A1' };
  XLSX.utils.book_append_sheet(workbook, sheet, name);
};

// One workbook with the three tabs of the supplier: current account, remitos line by line and goods bought.
const exportSupplierExcel = async (supplier: { id: number; name: string }) => {
  const [{ movements }, { receipts }] = await Promise.all([
    apiService.getAccountMovements('supplier', supplier.id),
    apiService.getSupplierReceipts(supplier.id),
  ]);
  const receiptList: SupplierReceiptSummary[] = receipts || [];
  const receiptNumbers = new Map(receiptList.map((r) => [r.id, r.receiptNumber || 'sin número']));
  const round = (n: number) => Math.round(Number(n || 0) * 100) / 100;
  const workbook = XLSX.utils.book_new();

  let balance = 0;
  const sortedMovements = [...(movements || [])].sort((a: any, b: any) => String(a.movementDate).localeCompare(String(b.movementDate)) || (a.movementType === b.movementType ? a.id - b.id : a.movementType === 'documento' ? -1 : 1));
  addSheet(workbook, 'Cuenta corriente', sortedMovements.map((m: any) => {
    const amount = Number(m.amount || 0);
    const counts = m.movementType === 'documento' || m.checkStatus !== 'rechazado';
    if (counts) balance += m.movementType === 'documento' ? amount : -amount;
    return {
      'Fecha': formatDMY(String(m.movementDate || '').slice(0, 10)),
      'Tipo': m.movementType === 'documento' ? 'Documento' : 'Pago',
      'Categoría': CATEGORY_LABELS[m.category] || m.category || '',
      'Remito': m.receiptId ? receiptNumbers.get(m.receiptId) || '' : '',
      'Debe (documentos)': m.movementType === 'documento' ? round(amount) : '',
      'Haber (pagos)': m.movementType === 'pago' ? round(amount) : '',
      'Saldo (le debo)': round(balance),
      'N° de cheque': m.checkNumber || '',
      'Banco': m.checkBank || '',
      'Emisión': m.checkIssueDate ? formatDMY(String(m.checkIssueDate).slice(0, 10)) : '',
      'Vencimiento': m.checkDueDate ? formatDMY(String(m.checkDueDate).slice(0, 10)) : '',
      'Estado del cheque': m.category === 'cheque' ? CHECK_STATUS_LABELS[m.checkStatus] || m.checkStatus || '' : '',
      'Nota': m.note || '',
    };
  }));

  const sortedReceipts = [...receiptList].sort((a, b) => String(a.receiptDate).localeCompare(String(b.receiptDate)) || a.id - b.id);
  addSheet(workbook, 'Remitos', sortedReceipts.flatMap<Record<string, string | number>>((r) => {
    const common = { 'N° remito': r.receiptNumber || 'sin número', 'Fecha': formatDMY(r.receiptDate) };
    if (!r.items.length) return [{ ...common, 'Monto del remito': round(r.amount), 'Medida': '', 'Color': '', 'Micrones': '', 'Packs': '', 'Bolsas': '', 'Precio unit.': '', 'Importe línea': '', 'Carga stock': '', 'Nota de la línea': '', 'Nota del remito': r.note || '' }];
    return r.items.map((item, index) => {
      const bags = receiptItemBags(item);
      return {
        ...common,
        // Only on the first line, so summing the column gives the real total.
        'Monto del remito': index === 0 ? round(r.amount) : '',
        'Medida': `${item.ancho} x ${item.largo} cm`,
        'Color': COLOR_LABELS[item.color as ColorKey] || item.color,
        'Micrones': item.microns || '',
        'Packs': round(bags / PACK_SIZE),
        'Bolsas': bags,
        'Precio unit.': item.unitPrice ? round(item.unitPrice) : '',
        'Importe línea': item.priceIsFigurative ? 'precio de referencia' : item.subtotal ? round(item.subtotal) : '',
        'Carga stock': item.loadStock === false ? 'No' : 'Sí',
        'Nota de la línea': [item.itemNote, item.priceFigurativeNote].filter(Boolean).join(' · '),
        'Nota del remito': index === 0 ? r.note || '' : '',
      };
    });
  }));

  const goods = new Map<string, { 'Medida': string; 'Color': string; 'Micrones': number | string; 'Packs': number; 'Bolsas': number; 'Ingresó a stock (bolsas)': number; 'Remitos': number; 'Última compra': string; last: string }>();
  sortedReceipts.forEach((r) => r.items.forEach((item) => {
    const bags = receiptItemBags(item);
    if (bags <= 0) return;
    const key = `${item.ancho}|${item.largo}|${String(item.color).toLowerCase()}|${Number(item.microns || 0)}`;
    const row = goods.get(key) || { 'Medida': `${item.ancho} x ${item.largo} cm`, 'Color': COLOR_LABELS[item.color as ColorKey] || item.color, 'Micrones': item.microns || '', 'Packs': 0, 'Bolsas': 0, 'Ingresó a stock (bolsas)': 0, 'Remitos': 0, 'Última compra': '', last: '' };
    row['Bolsas'] += bags;
    row['Packs'] = round(row['Bolsas'] / PACK_SIZE);
    if (item.loadStock !== false) row['Ingresó a stock (bolsas)'] += bags;
    row['Remitos'] += 1;
    if (r.receiptDate > row.last) { row.last = r.receiptDate; row['Última compra'] = formatDMY(r.receiptDate); }
    goods.set(key, row);
  }));
  addSheet(workbook, 'Mercadería comprada', Array.from(goods.values()).sort((a, b) => a['Medida'].localeCompare(b['Medida']) || a['Color'].localeCompare(b['Color'])).map(({ last, ...row }) => row));

  const safeName = supplier.name.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ]+/g, '-').replace(/^-|-$/g, '') || 'proveedor';
  XLSX.writeFile(workbook, `proveedor-${safeName}-${new Date().toLocaleDateString('sv-SE')}.xlsx`);
};

const formatPacks = (bags: number) => `${Math.floor(bags / PACK_SIZE).toLocaleString('es-AR')} packs${bags % PACK_SIZE ? ` + ${bags % PACK_SIZE} bolsas` : ''}`;

const ColorBadge: React.FC<{ color: string }> = ({ color }) => (
  <span className="inline-flex min-w-16 justify-center rounded px-2 py-0.5 text-xs font-semibold" style={receiptColorStyle(color)}>{COLOR_LABELS[color as ColorKey] || color}</span>
);

const SupplierPurchases: React.FC<{ supplierId: number }> = ({ supplierId }) => {
  const showWeight = useShowWeight();
  const [receipts, setReceipts] = useState<SupplierReceiptSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiService.getSupplierReceipts(supplierId)
      .then(({ receipts: list }) => { if (active) { setReceipts(list || []); setError(''); } })
      .catch((e: any) => { if (active) setError(e?.response?.data?.detail || 'No se pudieron cargar los remitos del proveedor.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [supplierId]);

  const purchases = useMemo(() => {
    const groups = new Map<string, { measure: string; color: string; microns: number; purchased: number; received: number; lastDate: string; bagWeight: number | null }>();
    receipts.forEach((receipt) => {
      (receipt.items || []).forEach((item) => {
        const quantity = Number(item.totalBolsas ?? (item.unidad === 'packs' ? item.cantidad * Number(item.bolsasPorPack || PACK_SIZE) : item.cantidad));
        if (quantity <= 0) return;
        const color = String(item.color || '');
        const microns = Number(item.microns || 0);
        const key = `${item.ancho}|${item.largo}|${color.toLowerCase()}|${microns}`;
        const current = groups.get(key) || { measure: `${item.ancho} × ${item.largo} cm`, color, microns, purchased: 0, received: 0, lastDate: '', bagWeight: bagWeightGrams(item.ancho, item.largo, microns) };
        current.purchased += quantity;
        if (item.loadStock !== false) current.received += quantity;
        if (receipt.receiptDate > current.lastDate) current.lastDate = receipt.receiptDate;
        groups.set(key, current);
      });
    });
    return Array.from(groups.values()).sort((a, b) => a.measure.localeCompare(b.measure) || a.color.localeCompare(b.color) || a.microns - b.microns);
  }, [receipts]);

  const totalBags = purchases.reduce((sum, item) => sum + item.purchased, 0);

  return (
    <section className="rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex flex-wrap items-end justify-between gap-2 border-b px-4 py-4 sm:px-5">
        <div>
          <h2 className="font-semibold text-gray-900">Mercadería comprada a este proveedor</h2>
          <p className="text-xs text-gray-500">Total histórico según sus remitos. Se informa aparte lo que no se marcó para ingresar al stock.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
        <WeightToggle />
        {!loading && purchases.length > 0 && <p className="text-sm text-gray-600">{receipts.length} remito{receipts.length === 1 ? '' : 's'} · <strong>{formatPacks(totalBags)}</strong></p>}
        </div>
      </div>
      {error && <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}
      {loading ? <div className="px-5 py-6 text-center text-gray-500">Cargando...</div> : !purchases.length ? (
        !error && <div className="px-5 py-10 text-center text-gray-500">Todavía no hay remitos con productos cargados.</div>
      ) : (<>
        <div className="divide-y md:hidden">
          {purchases.map((item) => (
            <div key={`${item.measure}-${item.color}-${item.microns}`} className="space-y-1 p-4">
              <div className="flex flex-wrap items-center gap-2"><span className="font-medium">{item.measure}</span>{item.microns > 0 && <span className="text-sm text-gray-400">{item.microns}μ</span>}<ColorBadge color={item.color} /></div>
              <p className="text-sm text-gray-700">{formatPacks(item.purchased)} <span className="text-gray-400">({item.purchased.toLocaleString('es-AR')} bolsas)</span></p>
              {item.received !== item.purchased && <p className="text-xs text-amber-700">{item.received.toLocaleString('es-AR')} bolsas ingresaron al stock</p>}
              {showWeight && item.bagWeight != null && <p className="text-xs text-gray-500">Peso est. {formatBagKg(item.bagWeight)} por bolsa · {formatKg(item.bagWeight * PACK_SIZE / 1000)} por pack · {formatKg(item.bagWeight * item.purchased / 1000)} en total</p>}
              <p className="text-xs text-gray-500">Última compra: {formatDMY(item.lastDate)}</p>
            </div>
          ))}
        </div>
        <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr><th className="px-5 py-3">Producto</th><th className="px-5 py-3 text-right">Packs</th><th className="px-5 py-3 text-right">Bolsas</th>{showWeight && <><th className="px-5 py-3 text-right" title="Ancho × largo × micrones × 2 × 0,92 ÷ 10.000. Debajo, el pack completo.">Peso est. bolsa / pack</th><th className="px-5 py-3 text-right">Kilos est.</th></>}<th className="px-5 py-3 text-right">Ingresó al stock</th><th className="px-5 py-3 text-right">Última compra</th></tr>
            </thead>
            <tbody className="divide-y">
              {purchases.map((item) => (
                <tr key={`${item.measure}-${item.color}-${item.microns}`}>
                  <td className="px-5 py-3"><div className="flex items-center gap-2"><span className="font-medium">{item.measure}</span>{item.microns > 0 && <span className="text-gray-400">· {item.microns}μ</span>}<ColorBadge color={item.color} /></div></td>
                  <td className="px-5 py-3 text-right">{formatPacks(item.purchased)}</td>
                  <td className="px-5 py-3 text-right text-gray-600">{item.purchased.toLocaleString('es-AR')}</td>
                  {showWeight && <>
                  <td className="px-5 py-3 text-right text-gray-600"><BagWeight grams={item.bagWeight} /></td>
                  <td className="px-5 py-3 text-right text-gray-600">{item.bagWeight == null ? '-' : formatKg(item.bagWeight * item.purchased / 1000)}</td>
                  </>}
                  <td className={`px-5 py-3 text-right ${item.received !== item.purchased ? 'font-medium text-amber-700' : 'text-gray-600'}`}>{item.received.toLocaleString('es-AR')}</td>
                  <td className="px-5 py-3 text-right text-gray-600">{formatDMY(item.lastDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}
    </section>
  );
};

const SupplierReceiptsList: React.FC<{ supplierId: number; isReadOnly: boolean }> = ({ supplierId, isReadOnly }) => {
  const showWeight = useShowWeight();
  const [receipts, setReceipts] = useState<SupplierReceiptSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<SupplierReceiptSummary | null>(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const { receipts: list } = await apiService.getSupplierReceipts(supplierId);
      setReceipts(list || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'No se pudieron cargar los remitos del proveedor.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  return (
    <section className="rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-4 sm:px-5">
        <div>
          <h2 className="font-semibold text-gray-900">Remitos</h2>
          <p className="text-xs text-gray-500">Lo que entregó este proveedor, con el detalle de cada remito.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <WeightToggle className="py-2" />
        {!isReadOnly && <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3 py-2 text-sm text-white hover:bg-primary-700"><Plus className="h-4 w-4" />Remito de proveedor</button>}
        </div>
      </div>
      {error && <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 sm:mx-5">{error}</div>}
      {loading ? (
        <div className="px-5 py-6 text-center text-gray-500">Cargando...</div>
      ) : receipts.length === 0 ? (
        !error && <div className="px-5 py-10 text-center text-gray-500">Todavía no hay remitos cargados para este proveedor.</div>
      ) : (
        <div className="space-y-4 p-3 sm:p-5">
          {receipts.map((r) => {
            const hasPrices = r.items.some((item) => item.unitPrice);
            const hasNotes = r.items.some((item) => item.itemNote || item.priceFigurativeNote);
            const hasNonStock = r.items.some((item) => item.loadStock === false);
            return (
              <div key={r.id} className="overflow-hidden rounded-xl border border-gray-200 border-l-4 border-l-primary-600 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 sm:px-4">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="inline-flex items-center gap-1.5 self-center font-semibold text-gray-900"><FileText className="h-4 w-4 text-gray-400" />Remito {r.receiptNumber || 'sin número'}</span>
                    <span className="text-xs text-gray-500">{formatDMY(r.receiptDate)}</span>
                    {r.amount > 0 ? <span className="text-sm font-semibold text-gray-900">{formatARS(r.amount)}</span> : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Sin monto</span>}
                    {r.hasNonStockItem && <span title="Alguna línea de este remito no carga a Stock propio" className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">No carga stock</span>}
                    {r.hasFigurativePriceItem && <span title="Alguna línea tiene precio solo de referencia, no incluido en el monto total" className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">Precio de ref.</span>}
                    {r.amountMismatch && <span title="El monto cargado no coincide con la suma de las líneas" className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">⚠ no coincide</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {r.attachmentPath && <a href={toProductImageUrl(r.attachmentPath)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"><Paperclip className="h-3.5 w-3.5" />Comprobante</a>}
                    {!isReadOnly && <button onClick={() => setEditing(r)} className="inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"><Pencil className="h-3.5 w-3.5" />Editar</button>}
                  </div>
                </div>
                <div className="space-y-2 p-3 sm:p-4">
                  {r.items.length ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 text-gray-500">
                          <tr>
                            <th className="px-3 py-1.5 text-left">Medida</th>
                            <th className="px-3 py-1.5 text-left">Color</th>
                            <th className="px-3 py-1.5 text-right">Packs</th>
                            <th className="px-3 py-1.5 text-right">Bolsas</th>
                            {showWeight && <th className="px-3 py-1.5 text-right" title="Ancho × largo × micrones × 2 × 0,92 ÷ 10.000. Debajo, el pack completo.">Peso est. bolsa / pack</th>}
                            {hasPrices && <><th className="px-3 py-1.5 text-right">Precio unit.</th><th className="px-3 py-1.5 text-right" title="Precio unitario del remito × 50 bolsas">Precio pack</th><th className="px-3 py-1.5 text-right">Importe</th></>}
                            {hasNotes && <th className="px-3 py-1.5 text-left">Nota</th>}
                            {hasNonStock && <th className="px-3 py-1.5 text-left">Stock propio</th>}
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {r.items.map((item, index) => {
                            const bags = Number(item.totalBolsas ?? (item.unidad === 'packs' ? item.cantidad * Number(item.bolsasPorPack || PACK_SIZE) : item.cantidad));
                            return (
                              <tr key={index} className={item.loadStock === false ? 'bg-amber-50' : undefined}>
                                <td className="whitespace-nowrap px-3 py-1.5 font-medium">{item.ancho} × {item.largo} cm{item.microns ? <span className="font-normal text-gray-400"> · {item.microns}μ</span> : null}</td>
                                <td className="px-3 py-1.5"><ColorBadge color={item.color} /></td>
                                <td className="px-3 py-1.5 text-right">{Math.floor(bags / PACK_SIZE)}{bags % PACK_SIZE ? ` + ${bags % PACK_SIZE}` : ''}</td>
                                <td className="px-3 py-1.5 text-right">{bags.toLocaleString('es-AR')}</td>
                                {showWeight && <td className="whitespace-nowrap px-3 py-1.5 text-right"><BagWeight grams={bagWeightGrams(item.ancho, item.largo, item.microns)} units={Number(item.bolsasPorPack) || PACK_SIZE} /></td>}
                                {hasPrices && <>
                                  <td className="px-3 py-1.5 text-right">{item.unitPrice ? formatARS(item.unitPrice) : '-'}</td>
                                  <td className="whitespace-nowrap px-3 py-1.5 text-right">{item.unitPrice ? formatARS(item.unitPrice * PACK_SIZE) : '-'}</td>
                                  <td className="px-3 py-1.5 text-right">{item.priceIsFigurative ? <span title="Precio de referencia, no suma al total" className="cursor-help text-gray-400 underline decoration-dotted">ref.</span> : item.subtotal ? formatARS(item.subtotal) : '-'}</td>
                                </>}
                                {hasNotes && <td className="px-3 py-1.5 text-gray-600">{[item.itemNote, item.priceFigurativeNote ? `Precio de referencia: ${item.priceFigurativeNote}` : ''].filter(Boolean).join(' · ') || '-'}</td>}
                                {hasNonStock && <td className="px-3 py-1.5">{item.loadStock === false ? <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700">No carga</span> : '-'}</td>}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="text-sm text-gray-500">Remito sin productos detallados.</p>}
                  {r.note && <p className="text-sm text-gray-600">{r.note}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {(creating || editing) && (
        <SupplierReceiptModal
          supplierId={supplierId}
          editingReceipt={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={load}
        />
      )}
    </section>
  );
};


type SupplierTab = 'cuenta' | 'remitos' | 'precios' | 'mercaderia';
const TABS: Array<{ key: SupplierTab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { key: 'cuenta', label: 'Cuenta corriente', icon: Wallet },
  { key: 'remitos', label: 'Remitos', icon: FileText },
  { key: 'precios', label: 'Lista de precios', icon: Tag },
  { key: 'mercaderia', label: 'Mercadería comprada', icon: Package },
];

interface ProveedorPaginaProps {
  supplierIdOverride?: number;
  backTo?: string;
  backLabel?: string;
}

const ProveedorPagina: React.FC<ProveedorPaginaProps> = ({ supplierIdOverride, backTo = '/admin/proveedores', backLabel = 'Volver a proveedores' }) => {
  const { supplierId } = useParams<{ supplierId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isReadOnly = !!user?.isReadOnly;
  const id = supplierIdOverride ?? Number(supplierId);

  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === searchParams.get('tab')) ? searchParams.get('tab') as SupplierTab : 'cuenta';
  const setTab = (next: SupplierTab) => setSearchParams(next === 'cuenta' ? {} : { tab: next }, { replace: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const response = await apiService.getSuppliers();
        const found = (response.suppliers || []).find((s: Supplier) => s.id === id) || null;
        if (!cancelled) {
          if (found) setSupplier(found);
          else setError('No se encontró el proveedor.');
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.detail || 'No se pudo cargar el proveedor.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const [receipts, setReceipts] = useState<SupplierReceiptSummary[] | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const currentYear = new Date().getFullYear();
  const exportExcel = async () => {
    if (!supplier) return;
    try {
      setExporting(true);
      setExportError('');
      await exportSupplierExcel(supplier);
    } catch (e: any) {
      setExportError(e?.response?.data?.detail || 'No se pudo exportar el Excel del proveedor.');
    } finally {
      setExporting(false);
    }
  };

  // Se recarga al cambiar de pestaña para reflejar remitos cargados o editados en ellas.
  useEffect(() => {
    let active = true;
    apiService.getSupplierReceipts(id)
      .then(({ receipts: list }) => { if (active) setReceipts(list || []); })
      .catch(() => { if (active) setReceipts(null); });
    return () => { active = false; };
  }, [id, tab]);

  const stats = useMemo(() => {
    if (!receipts) return null;
    const yearPrefix = `${currentYear}-`;
    const yearReceipts = receipts.filter((r) => String(r.receiptDate).startsWith(yearPrefix));
    const bags = receipts.reduce((sum, r) => sum + r.items.reduce((acc, item) => acc + Number(item.totalBolsas ?? (item.unidad === 'packs' ? item.cantidad * Number(item.bolsasPorPack || PACK_SIZE) : item.cantidad)), 0), 0);
    return {
      total: receipts.reduce((sum, r) => sum + Number(r.amount || 0), 0),
      count: receipts.length,
      yearTotal: yearReceipts.reduce((sum, r) => sum + Number(r.amount || 0), 0),
      yearCount: yearReceipts.length,
      bags,
      lastDate: receipts.reduce((last, r) => (r.receiptDate > last ? r.receiptDate : last), ''),
      withoutAmount: receipts.filter((r) => !(Number(r.amount) > 0)).length,
    };
  }, [receipts, currentYear]);

  const backButton =<button onClick={() => navigate(backTo)} className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"><ArrowLeft className="h-4 w-4" />{backLabel}</button>;

  if (loading) {
    return <div className="space-y-6"><p className="text-gray-600">Cargando proveedor...</p></div>;
  }

  if (error || !supplier) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error || 'No se encontró el proveedor.'}</div>
        {backButton}
      </div>
    );
  }

  const details = [supplier.contactName, supplier.phone, supplier.email, supplier.cuit ? `CUIT ${supplier.cuit}` : ''].filter(Boolean).join(' · ');

  return (
    <div className="space-y-6">
      {backButton}

      <div className="flex flex-col gap-2 rounded-xl border border-gray-100 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">{supplier.name}</h1>
          {!supplier.isActive && <span className="rounded-full border border-yellow-200 bg-yellow-100 px-2 py-1 text-xs font-semibold text-yellow-700">Inactivo</span>}
          <button onClick={exportExcel} disabled={exporting} className="ml-auto inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
            <Download className="h-4 w-4" />{exporting ? 'Exportando...' : 'Exportar a Excel'}
          </button>
        </div>
        {exportError && <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">{exportError}</div>}
        {details && <p className="break-words text-sm text-gray-600 sm:text-base">{details}</p>}
        {stats && (
          <div className="mt-2 grid grid-cols-2 gap-3 border-t border-gray-100 pt-4 lg:grid-cols-4">
            <div>
              <p className="text-xs text-gray-500">Compras totales</p>
              <p className="text-lg font-bold text-gray-900 sm:text-xl">{formatARS(stats.total)}</p>
              <p className="text-xs text-gray-500">{stats.count} remito{stats.count === 1 ? '' : 's'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Compras {currentYear}</p>
              <p className="text-lg font-bold text-gray-900 sm:text-xl">{formatARS(stats.yearTotal)}</p>
              <p className="text-xs text-gray-500">{stats.yearCount} remito{stats.yearCount === 1 ? '' : 's'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Mercadería comprada</p>
              <p className="text-lg font-bold text-gray-900 sm:text-xl">{Math.floor(stats.bags / PACK_SIZE).toLocaleString('es-AR')} packs</p>
              <p className="text-xs text-gray-500">{stats.bags.toLocaleString('es-AR')} bolsas</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Última compra</p>
              <p className="text-lg font-bold text-gray-900 sm:text-xl">{stats.lastDate ? formatDMY(stats.lastDate) : '-'}</p>
            </div>
            {stats.withoutAmount > 0 && <p className="col-span-2 text-xs text-amber-700 lg:col-span-4">{stats.withoutAmount} remito{stats.withoutAmount === 1 ? ' no tiene' : 's no tienen'} monto cargado y no {stats.withoutAmount === 1 ? 'suma' : 'suman'} en las compras.</p>}
          </div>
        )}
      </div>

      <nav className="flex gap-1 overflow-x-auto border-b border-gray-200">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`-mb-px inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium sm:px-4 ${tab === key ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800'}`}
          >
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </nav>

      {tab === 'cuenta' && <CuentaCorrienteDetalle partyTypeOverride="supplier" partyIdOverride={id} embedded />}
      {tab === 'remitos' && <SupplierReceiptsList supplierId={id} isReadOnly={isReadOnly} />}
      {tab === 'precios' && <SupplierPriceList supplierId={id} isReadOnly={isReadOnly} />}
      {tab === 'mercaderia' && <SupplierPurchases supplierId={id} />}
    </div>
  );
};

export default ProveedorPagina;
